import { useState } from "react";
import { CaptureModes } from "@enums/capture-modes";
import { CaptureStep } from "@idw-types/data/capture-step";
import { useSelector } from "react-redux";
import { getUseAcuantSDKCapture } from "@redux/selectors/verification-configuration-selectors";
import { NativeCaptureContainer } from "../native-capture-container/native-capture-container";
import { RelaunchContainer } from "../relaunch-container/relaunch-container";
import { AcuantDocumentCaptureContainer } from "../acuant-document-capture-container/acuant-document-capture-container";
import { AcuantFaceCaptureContainer } from "../acuant-face-capture-container/acuant-face-capture-container";
import { AcuantDocumentProcessingValidationResult } from "@idw-types/common/acuant-document-processing-result";
import { CaptureStepTypes } from "@enums/capture-step-types";
import { APValidationLivenessAssessmentResult } from "@idw-types/requests/ap-validation-liveness-assessment-result";
import { useAuditLogger } from "@hooks/use-audit-logger";
import { AuditLogTypes } from "@constants/audit-log-types";
import styles from "./generic-capture-container.less";

type GenericCaptureContainerProps = {
    captureStep: CaptureStep;
    captureMode: CaptureModes;
    setShowCaptureError: (showCaptureError: boolean) => void;
    setImageUrl: (imageUrl: string) => void;
    setAcuantValidationResults: (results: AcuantDocumentProcessingValidationResult) => void;
    setLivenessValidationResult: (result: APValidationLivenessAssessmentResult) => void;
    resetCaptureStepPage: () => void;
};

const ACUANT_DOCUMENT_CAPTURE_STEPS = [
    CaptureStepTypes.ID_CARD_BACK,
    CaptureStepTypes.ID_CARD_FRONT,
    CaptureStepTypes.DRIVER_LICENSE_BACK,
    CaptureStepTypes.DRIVER_LICENSE_FRONT,
    CaptureStepTypes.PASSPORT
];

const ACUANT_FACE_CAPTURE_STEPS = [
    CaptureStepTypes.FACE
];

export const GenericCaptureContainer = (props: GenericCaptureContainerProps) => {
    const {
        captureStep,
        captureMode,
        setShowCaptureError,
        setImageUrl,
        setAcuantValidationResults,
        setLivenessValidationResult,
        resetCaptureStepPage
    } = props;

    const { writeAuditLog } = useAuditLogger();

    const useAcuantSDKCapture = useSelector(getUseAcuantSDKCapture);

    const [state, setState] = useState({
        isCapturing: false,
        isProcessing: false
    });

    const setIsCapturingAndProcessing = (isCapturing: boolean, isProcessing: boolean) => {
        setState({ ...state, isCapturing, isProcessing });
        if (isCapturing || isProcessing) {
            setShowCaptureError(false);
        }
    };

    const handleOnClickRelaunch = () => {
        setState({ isCapturing: false, isProcessing: false });
        resetCaptureStepPage();
        writeAuditLog(AuditLogTypes.DCUI_DISCARD_BUTTON_HIT);
    };

    return (
        <div className={styles.genericCaptureContainer}>
            {captureMode === CaptureModes.NATIVE && (
                <NativeCaptureContainer
                    captureStep={captureStep}
                    isCapturing={state.isCapturing}
                    isProcessing={state.isProcessing}
                    setIsCapturingAndProcessing={setIsCapturingAndProcessing}
                    setImageUrl={setImageUrl}
                />
            )}
            {useAcuantSDKCapture && captureMode === CaptureModes.ACUANT_SDK && ACUANT_DOCUMENT_CAPTURE_STEPS.includes(captureStep.type) && (
                <AcuantDocumentCaptureContainer
                    captureStep={captureStep}
                    isCapturing={state.isCapturing}
                    isProcessing={state.isProcessing}
                    setIsCapturingAndProcessing={setIsCapturingAndProcessing}
                    setAcuantValidationResults={setAcuantValidationResults}
                    setImageUrl={setImageUrl}
                    setShowCaptureError={setShowCaptureError}
                />
            )}
            {useAcuantSDKCapture && captureMode === CaptureModes.ACUANT_SDK && ACUANT_FACE_CAPTURE_STEPS.includes(captureStep.type) && (
                <AcuantFaceCaptureContainer
                    captureStep={captureStep}
                    isCapturing={state.isCapturing}
                    isProcessing={state.isProcessing}
                    setShowCaptureError={setShowCaptureError}
                    setIsCapturingAndProcessing={setIsCapturingAndProcessing}
                    setLivenessValidationResult={setLivenessValidationResult}
                    setImageUrl={setImageUrl}
                />
            )}
            {(state.isCapturing || state.isProcessing) && (
                <RelaunchContainer
                    isCapturing={state.isCapturing}
                    isProcessing={state.isProcessing}
                    onClickRelaunch={handleOnClickRelaunch}
                />
            )}
        </div>
    );
};